// Player API client (docs/03 §Player — the only routes a device token can reach).
//
// Three calls: pair/start (unauthenticated), pair/poll (unauthenticated, returns
// the token once), and manifest (bearer-authed, ETag-conditional, carries the
// heartbeat as request headers). No retry logic lives here — the state machine
// owns the poll cadence and backoff so this stays a thin, typed transport.

import type { Config } from './config.ts';
import { apiUrl } from './config.ts';
import type { PlaybackEvent } from './events.ts';
import type { SnapshotKind } from './snapshot.ts';
import type { Manifest, PairResult } from './types.ts';

export const PLAYER_VERSION = '0.1.0';

/** Max snapshot upload; larger captures are downscaled before reaching here. */
export const SNAPSHOT_MAX_BYTES = 2 * 1024 * 1024;

/** Thrown on 401 so the state machine can wipe storage and return to PAIRING. */
export class UnauthorizedError extends Error {
  constructor() {
    super('device token revoked (401)');
    this.name = 'UnauthorizedError';
  }
}

/** Heartbeat snapshot sent as headers on every manifest poll (docs/04 §Heartbeat). */
export interface Heartbeat {
  uptimeS: number;
  currentItem: string | null;
  cacheOk: boolean;
  /**
   * Sprint 11 (architect ruling: always-send, mirroring `X-Cache-Ok`): when
   * `storageUsage` is populated EVERY poll carries `X-Storage-Pressure: 1|0` and
   * `X-Storage-Usage: <usedBytes>/<quotaBytes>` (exact header names coordinated
   * with the API/D3). The pressure bit is `1` only while a swap is being refused
   * for lack of quota; usage is `navigator.storage.estimate()` used/quota. An
   * ABSENT header set means a pre-Sprint-11 player, so it can't double as "not
   * pressured" — hence the explicit `0`.
   */
  storagePressure?: boolean;
  /** `<usedBytes>/<quotaBytes>` from estimate(); its presence gates both headers. */
  storageUsage?: string;
}

export interface ManifestResponse {
  /** null when the server returned 304 Not Modified. */
  manifest: Manifest | null;
  etag: string | null;
  /** `X-SeenCast-Command` value (e.g. 'snapshot'), present on 200 AND 304. */
  command: string | null;
}

/** Snapshot upload payload (raw jpeg body + dimensions, or empty for unsupported). */
export interface SnapshotUpload {
  kind: SnapshotKind;
  body: Blob | null;
  width: number;
  height: number;
}

/**
 * The transport contract used by the state machine. Implemented by `PlayerApi`
 * (real HTTP) and by the mock (`?mock=1`, bundled fixtures, no backend) so the
 * exact same PAIRING→RUNNING code path is exercised in both.
 */
export interface PlayerApiLike {
  pairStart(deviceHint: string): Promise<{ code: string }>;
  pairPoll(code: string): Promise<PairResult | null>;
  fetchManifest(token: string, etag: string | null, hb: Heartbeat): Promise<ManifestResponse>;
  /** POST a batch of playback events; resolves only on 2xx (batch acknowledged). */
  postEvents(token: string, events: PlaybackEvent[]): Promise<void>;
  /** POST a screen snapshot (raw jpeg body + kind/dims headers). */
  postSnapshot(token: string, snap: SnapshotUpload): Promise<void>;
}

export class PlayerApi implements PlayerApiLike {
  constructor(private cfg: Config) {}

  /** POST /v1/player/pair/start → { code }. */
  async pairStart(deviceHint: string): Promise<{ code: string }> {
    const res = await fetch(apiUrl(this.cfg, '/v1/player/pair/start'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ device_hint: deviceHint }),
    });
    if (!res.ok) throw new Error(`pair/start ${res.status}`);
    return (await res.json()) as { code: string };
  }

  /**
   * GET /v1/player/pair/poll?code= → 204 (still waiting) → null, or the token
   * once the operator binds the code in the CMS.
   */
  async pairPoll(code: string): Promise<PairResult | null> {
    const res = await fetch(
      apiUrl(this.cfg, `/v1/player/pair/poll?code=${encodeURIComponent(code)}`),
    );
    if (res.status === 204) return null;
    if (!res.ok) throw new Error(`pair/poll ${res.status}`);
    return (await res.json()) as PairResult;
  }

  /**
   * GET /v1/player/manifest with If-None-Match + heartbeat headers.
   * 304 → { manifest: null }. 401 → UnauthorizedError. 200 → parsed manifest.
   */
  async fetchManifest(
    token: string,
    etag: string | null,
    hb: Heartbeat,
  ): Promise<ManifestResponse> {
    const headers: Record<string, string> = {
      authorization: `Bearer ${token}`,
      'X-Player-Version': PLAYER_VERSION,
      'X-Uptime': String(hb.uptimeS),
      'X-Cache-Ok': hb.cacheOk ? '1' : '0',
    };
    if (hb.currentItem) headers['X-Current-Item'] = hb.currentItem;
    // Always-send when storage is being reported (populated in RUNNING): the bit
    // mirrors X-Cache-Ok's 1|0 shape so absent == pre-Sprint-11 player, not "ok".
    if (hb.storageUsage !== undefined) {
      headers['X-Storage-Pressure'] = hb.storagePressure ? '1' : '0';
      headers['X-Storage-Usage'] = hb.storageUsage;
    }
    if (etag) headers['If-None-Match'] = etag;

    const res = await fetch(apiUrl(this.cfg, '/v1/player/manifest'), { headers });
    // The server may piggyback a command (e.g. 'snapshot') on the poll response —
    // on a fresh manifest (200) or a 304, so it reaches idle-but-current screens.
    const command = res.headers.get('X-SeenCast-Command');
    if (res.status === 304) return { manifest: null, etag, command };
    if (res.status === 401) throw new UnauthorizedError();
    if (!res.ok) throw new Error(`manifest ${res.status}`);
    const manifest = (await res.json()) as Manifest;
    // Prefer the response ETag header; fall back to the manifest's own version.
    const newEtag = res.headers.get('etag') ?? manifest.version ?? null;
    return { manifest, etag: newEtag, command };
  }

  /** POST /v1/player/events { events } — resolves only on 2xx (batch acknowledged). */
  async postEvents(token: string, events: PlaybackEvent[]): Promise<void> {
    const res = await fetch(apiUrl(this.cfg, '/v1/player/events'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ events }),
      keepalive: true,
    });
    if (!res.ok) throw new Error(`events ${res.status}`);
  }

  /** POST /v1/player/snapshot — raw jpeg body (empty for `unsupported`) + metadata headers. */
  async postSnapshot(token: string, snap: SnapshotUpload): Promise<void> {
    const headers: Record<string, string> = {
      authorization: `Bearer ${token}`,
      'content-type': 'image/jpeg',
      'X-Snapshot-Kind': snap.kind,
      'X-Snapshot-Width': String(snap.width),
      'X-Snapshot-Height': String(snap.height),
    };
    const res = await fetch(apiUrl(this.cfg, '/v1/player/snapshot'), {
      method: 'POST',
      headers,
      body: snap.body ?? new Blob([]),
    });
    if (!res.ok) throw new Error(`snapshot ${res.status}`);
  }
}
